import { useState } from 'react';
import { Mail, Phone, MapPin, Send, User, MessageSquare } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { eventInfo } from '../mock';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({ ...prev, subject: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/contact/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to submit message');
      }

      const data = await response.json();
      
      toast.success('Message sent successfully!', {
        description: 'We\'ll get back to you as soon as possible.'
      });
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      toast.error('Failed to send message', {
        description: 'Please try again later or email us directly.'
      });
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.pexels.com/photos/7516219/pexels-photo-7516219.jpeg?auto=compress&cs=tinysrgb&w=1920"
            alt="Family with dog in park"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-white/95 via-cyan-50/90 to-white"></div>
        </div>
        
        <div className="relative container mx-auto px-4 py-16 md:py-20">
          <div className="max-w-3xl mx-auto text-center">
            <Badge className="mb-4 bg-cyan-100 text-cyan-800 hover:bg-cyan-200 border-cyan-200">
              Get In Touch
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 tracking-tight">
              Contact Us
            </h1>
            <p className="text-lg text-slate-600">
              Have questions about the event? Want to volunteer or become a sponsor? 
              We'd love to hear from you!
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-5 gap-8">
              {/* Contact Information */}
              <div className="lg:col-span-2 space-y-6">
                <Card className="border-slate-200">
                  <CardHeader>
                    <CardTitle className="text-2xl">Event Information</CardTitle>
                    <CardDescription>
                      Get in touch with the organizing committee
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="flex items-start space-x-4">
                      <div className="bg-cyan-100 rounded-full p-3 flex-shrink-0">
                        <MapPin className="h-5 w-5 text-cyan-700" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-800 mb-1">Location</h3>
                        <p className="text-sm text-slate-600">{eventInfo.location}</p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4">
                      <div className="bg-teal-100 rounded-full p-3 flex-shrink-0">
                        <Mail className="h-5 w-5 text-teal-700" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-800 mb-1">Email</h3>
                        <a 
                          href="mailto:paws@twra.net" 
                          className="text-sm text-cyan-600 hover:text-cyan-700 hover:underline"
                        >
                          paws@twra.net
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4">
                      <div className="bg-cyan-100 rounded-full p-3 flex-shrink-0">
                        <Phone className="h-5 w-5 text-cyan-700" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-800 mb-1">Phone</h3>
                        <p className="text-sm text-slate-600">Available upon request</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-cyan-50 to-teal-50 border-cyan-200">
                  <CardHeader>
                    <CardTitle className="text-lg">Hosted By</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="font-semibold text-slate-800 mb-2">
                      Twin Waters Resident's Association
                    </p>
                    <p className="text-sm text-slate-600">
                      Dedicated to bringing our community together through meaningful events and initiatives.
                    </p>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-slate-800 to-slate-900 text-white border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-lg">Volunteer & Sponsor</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm text-slate-200">
                    <p className="flex items-start">
                      <span className="text-cyan-400 mr-2">•</span>
                      Help with event setup and coordination
                    </p>
                    <p className="flex items-start">
                      <span className="text-cyan-400 mr-2">•</span>
                      Sponsor activities and support the cause
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Contact Form */}
              <div className="lg:col-span-3">
                <Card className="border-slate-200">
                  <CardHeader>
                    <CardTitle className="text-2xl">Send Us a Message</CardTitle>
                    <CardDescription>
                      Fill out the form below and we'll respond within 24-48 hours
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="name">Full Name *</Label>
                          <div className="relative">
                            <User className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                            <Input
                              id="name"
                              name="name"
                              value={formData.name}
                              onChange={handleInputChange}
                              required
                              placeholder="John Smith"
                              className="pl-10"
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="email">Email Address *</Label>
                          <div className="relative">
                            <Mail className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                            <Input
                              id="email"
                              name="email"
                              type="email"
                              value={formData.email}
                              onChange={handleInputChange}
                              required
                              placeholder="john@example.com"
                              className="pl-10"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="phone">Phone Number</Label>
                        <div className="relative">
                          <Phone className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                          <Input
                            id="phone"
                            name="phone"
                            type="tel"
                            value={formData.phone}
                            onChange={handleInputChange}
                            placeholder="0400 000 000 (optional)"
                            className="pl-10"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="subject">Subject *</Label>
                        <Select value={formData.subject} onValueChange={handleSelectChange}>
                          <SelectTrigger>
                            <SelectValue placeholder="What is your inquiry about?" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="general">General Inquiry</SelectItem>
                            <SelectItem value="competition">Competition Entry Question</SelectItem>
                            <SelectItem value="vendor">Vendor/Sponsor Opportunity</SelectItem>
                            <SelectItem value="volunteer">Volunteer Information</SelectItem>
                            <SelectItem value="media">Media & Press</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="message">Message *</Label>
                        <div className="relative">
                          <MessageSquare className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                          <Textarea
                            id="message"
                            name="message"
                            value={formData.message}
                            onChange={handleInputChange}
                            required
                            placeholder="Tell us more about your inquiry..."
                            rows={6}
                            className="pl-10 pt-3"
                          />
                        </div>
                      </div>

                      <Button 
                        type="submit" 
                        className="w-full bg-gradient-to-r from-cyan-600 to-teal-600 hover:from-cyan-700 hover:to-teal-700"
                        size="lg"
                      >
                        <Send className="mr-2 h-5 w-5" />
                        Send Message
                      </Button>

                      <p className="text-xs text-slate-500 text-center">
                        We respect your privacy. Your information will only be used to respond to your inquiry.
                      </p>
                    </form>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
